(() => {
  const COMPONENT_VERSION = '10';
  if (window.RoomdenSizeGuide?.version === COMPONENT_VERSION) {
    window.RoomdenSizeGuide.initAll();
    return;
  }

  const STORAGE_KEY = 'roomden:size-guide-system';
  const LEGACY_STORAGE_KEY = 'roomden:size-guide-unit';
  const SCROLL_EPSILON = 1;
  let rtlScrollType;

  function getRtlScrollType() {
    if (rtlScrollType) return rtlScrollType;
    if (!document.body) return 'negative';

    const probe = document.createElement('div');
    const content = document.createElement('div');
    probe.dir = 'rtl';
    probe.style.cssText = 'block-size:1px;inline-size:4px;overflow:scroll;position:absolute;visibility:hidden;';
    content.style.cssText = 'block-size:1px;inline-size:8px;';
    probe.append(content);
    document.body.append(probe);

    if (probe.scrollLeft > 0) {
      rtlScrollType = 'default';
    } else {
      probe.scrollLeft = 1;
      rtlScrollType = probe.scrollLeft === 0 ? 'negative' : 'reverse';
    }

    probe.remove();
    return rtlScrollType;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function distanceFromInlineStart(wrapper, direction) {
    const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
    if (direction !== 'rtl') return clamp(wrapper.scrollLeft, 0, maximum);
    if (getRtlScrollType() === 'negative') return clamp(-wrapper.scrollLeft, 0, maximum);
    return clamp(maximum - wrapper.scrollLeft, 0, maximum);
  }

  function setDistanceFromInlineStart(wrapper, direction, distance) {
    const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
    const nextDistance = clamp(distance, 0, maximum);
    if (direction !== 'rtl') {
      wrapper.scrollLeft = nextDistance;
    } else if (getRtlScrollType() === 'negative') {
      wrapper.scrollLeft = -nextDistance;
    } else {
      wrapper.scrollLeft = maximum - nextDistance;
    }
  }

  function scrollHintForState(scrollable, atStart, atEnd) {
    if (!scrollable) return '';
    const action = window.matchMedia('(max-width: 749px)').matches ? 'Swipe' : 'Scroll horizontally';
    if (atEnd && !atStart) return `${action} back to view all measurements`;
    if (!atStart && !atEnd) return `${action} to review all measurements`;
    return `${action} to view all measurements`;
  }

  function ensurePersistentScrollbar(wrapper) {
    let track = wrapper.nextElementSibling?.classList.contains('rd-size-guide__scrollbar')
      ? wrapper.nextElementSibling
      : wrapper.previousElementSibling?.classList.contains('rd-size-guide__scrollbar')
        ? wrapper.previousElementSibling
        : null;
    if (track) {
      if (track !== wrapper.nextElementSibling) wrapper.after(track);
      track.dataset.tableLayout = wrapper.dataset.tableLayout;
      track.dataset.columnCount = wrapper.dataset.columnCount;
      bindPersistentScrollbar(wrapper, track);
      return track;
    }

    track = document.createElement('div');
    track.className = 'rd-size-guide__scrollbar';
    track.setAttribute('aria-hidden', 'true');
    track.innerHTML = '<span class="rd-size-guide__scrollbar-thumb"></span>';
    track.dataset.tableLayout = wrapper.dataset.tableLayout;
    track.dataset.columnCount = wrapper.dataset.columnCount;
    wrapper.after(track);
    bindPersistentScrollbar(wrapper, track);
    return track;
  }

  function bindPersistentScrollbar(wrapper, track) {
    if (track.dataset.roomdenScrollbarReady === 'true') return;
    track.dataset.roomdenScrollbarReady = 'true';

    const thumb = track.querySelector('.rd-size-guide__scrollbar-thumb');
    let dragState = null;

    thumb.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const direction = window.getComputedStyle(wrapper).direction === 'rtl' ? 'rtl' : 'ltr';
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startDistance: distanceFromInlineStart(wrapper, direction),
        direction
      };
      track.dataset.dragging = 'true';
      thumb.setPointerCapture(event.pointerId);
    });

    thumb.addEventListener('pointermove', (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      const travel = Math.max(1, track.clientWidth - thumb.offsetWidth);
      const nextDistance = dragState.startDistance + ((event.clientX - dragState.startX) / travel) * maximum;
      setDistanceFromInlineStart(wrapper, dragState.direction, nextDistance);
    });

    const stopDragging = (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      if (thumb.hasPointerCapture(event.pointerId)) thumb.releasePointerCapture(event.pointerId);
      dragState = null;
      delete track.dataset.dragging;
    };

    thumb.addEventListener('pointerup', stopDragging);
    thumb.addEventListener('pointercancel', stopDragging);

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target === thumb) return;
      event.preventDefault();
      const direction = window.getComputedStyle(wrapper).direction === 'rtl' ? 'rtl' : 'ltr';
      const bounds = track.getBoundingClientRect();
      const travel = Math.max(1, track.clientWidth - thumb.offsetWidth);
      const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      const thumbCenter = clamp(event.clientX - bounds.left - thumb.offsetWidth / 2, 0, travel);
      setDistanceFromInlineStart(wrapper, direction, (thumbCenter / travel) * maximum);
      scheduleTableScrollState(wrapper);
    });
  }

  function updatePersistentScrollbar(wrapper, scrollable, distance, maximum) {
    const track = ensurePersistentScrollbar(wrapper);
    track.hidden = !scrollable;
    if (!scrollable) return;

    const thumb = track.querySelector('.rd-size-guide__scrollbar-thumb');
    const thumbRatio = clamp(wrapper.clientWidth / wrapper.scrollWidth, 0.16, 1);
    thumb.style.width = `${thumbRatio * 100}%`;

    window.requestAnimationFrame(() => {
      const progress = maximum > 0 ? distance / maximum : 0;
      const travel = Math.max(0, track.clientWidth - thumb.offsetWidth);
      thumb.style.transform = `translate(${travel * progress}px, -50%)`;
    });
  }

  function updateTableScrollState(wrapper) {
    const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
    const scrollable = maximum > SCROLL_EPSILON;
    const direction = window.getComputedStyle(wrapper).direction === 'rtl' ? 'rtl' : 'ltr';
    const distance = scrollable ? distanceFromInlineStart(wrapper, direction) : 0;
    const atStart = !scrollable || distance <= SCROLL_EPSILON;
    const atEnd = !scrollable || distance >= maximum - SCROLL_EPSILON;
    const hint = wrapper.previousElementSibling?.classList.contains('rd-size-guide__scroll-hint')
      ? wrapper.previousElementSibling
      : null;

    wrapper.dataset.scrollable = String(scrollable);
    wrapper.dataset.atStart = String(atStart);
    wrapper.dataset.atEnd = String(atEnd);
    wrapper.dataset.scrollDirection = direction;
    const baseLabel = wrapper.dataset.baseLabel || 'Size table';
    wrapper.setAttribute('aria-label', scrollable ? `${baseLabel}, scroll horizontally to view all columns` : baseLabel);
    updatePersistentScrollbar(wrapper, scrollable, distance, maximum);

    if (!hint) return;
    hint.hidden = !scrollable;
    hint.dataset.scrollable = String(scrollable);
    hint.dataset.atStart = String(atStart);
    hint.dataset.atEnd = String(atEnd);
    hint.textContent = scrollHintForState(scrollable, atStart, atEnd);
  }

  function scheduleTableScrollState(wrapper) {
    if (wrapper._roomdenScrollStateFrame) return;
    wrapper._roomdenScrollStateFrame = window.requestAnimationFrame(() => {
      wrapper._roomdenScrollStateFrame = null;
      updateTableScrollState(wrapper);
    });
  }

  function observeTableScrollState(wrapper) {
    if (wrapper.dataset.roomdenScrollStateReady === 'true') return;
    wrapper.dataset.roomdenScrollStateReady = 'true';

    const update = () => scheduleTableScrollState(wrapper);
    wrapper.addEventListener('scroll', update, { passive: true });
    wrapper.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const maximum = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
      if (maximum <= SCROLL_EPSILON) return;
      event.preventDefault();
      const direction = window.getComputedStyle(wrapper).direction === 'rtl' ? 'rtl' : 'ltr';
      const current = distanceFromInlineStart(wrapper, direction);
      const step = Math.max(88, wrapper.clientWidth * 0.72);
      let next = current;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = maximum;
      if (event.key === 'ArrowLeft') next = current - step;
      if (event.key === 'ArrowRight') next = current + step;
      setDistanceFromInlineStart(wrapper, direction, next);
      scheduleTableScrollState(wrapper);
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      observer.observe(wrapper);
      wrapper.querySelector('table') && observer.observe(wrapper.querySelector('table'));
      wrapper._roomdenScrollStateObserver = observer;
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    updateTableScrollState(wrapper);
  }

  function updateTableScrollStates(container) {
    container.querySelectorAll('.rd-size-guide__table-scroll').forEach(updateTableScrollState);
  }

  function savedSystem() {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      if (value === 'metric' || value === 'imperial') return value;
      const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      return legacy === 'in' ? 'imperial' : legacy === 'cm' ? 'metric' : null;
    } catch (error) {
      return null;
    }
  }

  function storeSystem(system) {
    try {
      window.localStorage.setItem(STORAGE_KEY, system);
    } catch (error) {
      // Browsing can continue when storage is unavailable.
    }
  }

  function supportsUnits(container) {
    return Boolean(container.querySelector('[data-metric][data-imperial], [data-cm][data-in], .unit-content-cm, .unit-content-in'));
  }

  function tableMatrix(table) {
    if (!table) return null;
    const headers = Array.from(table.tHead?.rows[0]?.cells || []).map((cell) => cell.textContent.trim());
    const rows = Array.from(table.tBodies?.[0]?.rows || []).map((row) =>
      Array.from(row.cells).map((cell) => cell.textContent.trim())
    );
    return headers.length && rows.length ? { headers, rows } : null;
  }

  function findColumn(headers, matcher) {
    return headers.findIndex((header) => matcher(header.replace(/\s+/g, ' ').trim()));
  }

  function formatBeddingDimension(value, system) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || text === '—') return '—';
    const normalized = text.replace(/&quot;/g, '"');
    const match = normalized.match(/(\d+(?:\.\d+)?)\s*(?:"|in)?\s*[×x]\s*(\d+(?:\.\d+)?)\s*(?:"|in|cm)?(.*)$/i);
    if (!match) return normalized;
    const suffix = match[3].trim();
    return `${match[1]} × ${match[2]} ${system === 'imperial' ? 'in' : 'cm'}${suffix ? ` ${suffix}` : ''}`;
  }

  function createBeddingTable(headers, rows) {
    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headers.forEach((label) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      headRow.append(cell);
    });
    thead.append(headRow);

    const tbody = document.createElement('tbody');
    rows.forEach((values) => {
      const row = document.createElement('tr');
      values.forEach((value, index) => {
        const cell = document.createElement(index === 0 ? 'th' : 'td');
        if (index === 0) cell.scope = 'row';
        cell.textContent = value;
        row.append(cell);
      });
      tbody.append(row);
    });
    table.append(thead, tbody);
    return table;
  }

  function normalizeBeddingSize(value) {
    return String(value || '').toLowerCase().replace(/\s|\+/g, '');
  }

  function selectedProductOption(prefix) {
    return document.querySelector(`input[type="radio"][name^="${prefix}-"]:checked`)?.value || '';
  }

  function defaultBeddingRegion() {
    const country = String(window.Shopify?.country || '').toUpperCase();
    if (country === 'AU' || country === 'NZ') return 'au';
    if (country === 'GB') return 'uk';
    if (country && !['US', 'CA'].includes(country)) return 'eu';
    return 'us';
  }

  function findTableByHeaders(tables, requiredMatchers) {
    return tables.find((table) => {
      const matrix = tableMatrix(table);
      if (!matrix) return false;
      return requiredMatchers.every((matcher) => matrix.headers.some((header) => matcher(header)));
    }) || null;
  }

  function systemTables(container, systemClass) {
    return Array.from(container.querySelectorAll(`.${systemClass} table`));
  }

  function findContractTable(tables, tableRole, fallbackMatchers) {
    return tables.find((table) => table.dataset.sizeGuideTable === tableRole) ||
      findTableByHeaders(tables, fallbackMatchers);
  }

  function simplifyLegacyBedding(container) {
    if (container.dataset.roomdenBeddingSimple === 'true') return;
    const metricTables = systemTables(container, 'unit-content-cm');
    const imperialTables = systemTables(container, 'unit-content-in');
    const compatibilityMatchers = [
      (header) => header.replace(/\s+/g, ' ').trim().toLowerCase().includes('roomden size'),
      (header) => header.includes('US / CA'),
      (header) => /\bAU\b/.test(header),
      (header) => /\bUK\b/.test(header),
      (header) => /\bEU\b/.test(header)
    ];
    const productMatchers = [
      (header) => header.toLowerCase().includes('duvet cover'),
      (header) => header.toLowerCase().includes('fitted sheet'),
      (header) => header.toLowerCase().includes('flat sheet'),
      (header) => header.toLowerCase().includes('pillow')
    ];
    const compatibilityMetricTable = findContractTable(metricTables, 'compatibility', compatibilityMatchers);
    const productMetricTable = findContractTable(metricTables, 'product-dimensions', productMatchers);
    const productImperialTable = findContractTable(imperialTables, 'product-dimensions', productMatchers);
    const compatibilityMetric = tableMatrix(compatibilityMetricTable);
    const productMetric = tableMatrix(productMetricTable);
    const productImperial = tableMatrix(productImperialTable);
    if (!compatibilityMetric || !productMetric || !productImperial) return;

    const compatibilityColumns = {
      us: findColumn(compatibilityMetric.headers, (header) => header.includes('US / CA')),
      au: findColumn(compatibilityMetric.headers, (header) => /\bAU\b/.test(header)),
      uk: findColumn(compatibilityMetric.headers, (header) => /\bUK\b/.test(header)),
      eu: findColumn(compatibilityMetric.headers, (header) => /\bEU\b/.test(header))
    };
    if (Object.values(compatibilityColumns).some((index) => index < 1)) return;

    const sizeColumn = 0;
    const metricColumns = {
      duvet: findColumn(productMetric.headers, (header) => header.toLowerCase().includes('duvet cover')),
      fitted: findColumn(productMetric.headers, (header) => header.toLowerCase().includes('fitted sheet')),
      flat: findColumn(productMetric.headers, (header) => header.toLowerCase().includes('flat sheet')),
      pillow: findColumn(productMetric.headers, (header) => header.toLowerCase().includes('pillow'))
    };
    const imperialColumns = {
      duvet: findColumn(productImperial.headers, (header) => header.toLowerCase().includes('duvet cover')),
      fitted: findColumn(productImperial.headers, (header) => header.toLowerCase().includes('fitted sheet')),
      flat: findColumn(productImperial.headers, (header) => header.toLowerCase().includes('flat sheet')),
      pillow: findColumn(productImperial.headers, (header) => header.toLowerCase().includes('pillow'))
    };
    if ([...Object.values(metricColumns), ...Object.values(imperialColumns)].some((index) => index < 1)) return;

    const intro = document.createElement('div');
    intro.className = 'size-guide-intro';
    intro.innerHTML = '<h4>Find Your Bedding Size</h4><p>Match your mattress first, then check the exact product measurements below.</p>';

    const compatibilitySection = document.createElement('section');
    compatibilitySection.className = 'rd-size-guide__decision-section';
    const compatibilityHeader = document.createElement('div');
    compatibilityHeader.className = 'rd-size-guide__decision-heading';
    compatibilityHeader.innerHTML = '<div><span class="rd-size-guide__step">01</span><h4>Choose Your Size</h4></div>';
    const regionLabel = document.createElement('label');
    regionLabel.className = 'rd-size-guide__field';
    regionLabel.innerHTML = '<span>Region</span><select data-bedding-region aria-label="Mattress size region"><option value="us">US / CA</option><option value="au">AU / NZ</option><option value="uk">UK</option><option value="eu">EU</option></select>';
    compatibilityHeader.append(regionLabel);
    const compatibilityTable = createBeddingTable(['Roomden size', 'US / CA mattress'], compatibilityMetric.rows.map((row) => [row[0], row[compatibilityColumns.us]]));
    compatibilityTable.dataset.beddingCompatibilityTable = '';
    const compatibilityNote = document.createElement('p');
    compatibilityNote.className = 'rd-size-guide__decision-note';
    compatibilityNote.textContent = 'Choose the Roomden size that matches your local mattress name.';
    compatibilitySection.append(compatibilityHeader, compatibilityTable, compatibilityNote);

    const productSection = document.createElement('section');
    productSection.className = 'rd-size-guide__decision-section';
    const productHeader = document.createElement('div');
    productHeader.className = 'rd-size-guide__decision-heading';
    productHeader.innerHTML = '<div><span class="rd-size-guide__step">02</span><h4>Product Measurements</h4></div>';
    const sheetLabel = document.createElement('label');
    sheetLabel.className = 'rd-size-guide__field';
    sheetLabel.innerHTML = '<span>Sheet</span><select data-bedding-sheet aria-label="Sheet type"><option value="flat">Flat</option><option value="fitted">Fitted</option></select>';
    productHeader.append(sheetLabel);
    const productRows = productMetric.rows.map((row, rowIndex) => [
      row[sizeColumn],
      row[metricColumns.duvet],
      row[metricColumns.flat],
      row[metricColumns.pillow],
      productImperial.rows[rowIndex]
    ]);
    const productTable = createBeddingTable(['Size', 'Duvet cover', 'Flat sheet', 'Pillowcases'], productRows.map((row) => row.slice(0, 4)));
    productTable.dataset.beddingProductTable = '';
    const advice = document.createElement('p');
    advice.className = 'rd-size-guide__decision-note';
    advice.innerHTML = 'For fitted sheets, also check mattress depth: <span data-metric="30 cm" data-imperial="12 in">30 cm</span>.';
    productSection.append(productHeader, productTable, advice);

    const referenceLabels = ['US / CA', 'AU / NZ', 'UK', 'EU'];
    const referenceMatchers = [
      (header) => header.trim().toLowerCase() === 'name',
      (header) => header.replace(/\s+/g, ' ').trim().toLowerCase() === 'w × l'
    ];
    const metricReferenceTables = metricTables.filter((table) =>
      table.dataset.sizeGuideTable === 'mattress-reference' ||
      (table !== compatibilityMetricTable && table !== productMetricTable && findTableByHeaders([table], referenceMatchers))
    );
    const imperialReferenceTables = imperialTables.filter((table) =>
      table.dataset.sizeGuideTable === 'mattress-reference' ||
      (table !== productImperialTable && findTableByHeaders([table], referenceMatchers))
    );
    const referenceDetails = document.createElement('details');
    referenceDetails.className = 'rd-size-guide__reference-details';
    const referenceSummary = document.createElement('summary');
    referenceSummary.innerHTML = '<span>Country mattress size reference</span><small>Optional</small>';
    const referenceContent = document.createElement('div');
    referenceContent.className = 'rd-size-guide__reference-content';
    referenceLabels.forEach((label, referenceIndex) => {
      const metricReference = tableMatrix(
        metricReferenceTables.find((table) => table.dataset.sizeGuideRegion === label) || metricReferenceTables[referenceIndex]
      );
      const imperialReference = tableMatrix(
        imperialReferenceTables.find((table) => table.dataset.sizeGuideRegion === label) || imperialReferenceTables[referenceIndex]
      );
      if (!metricReference || !imperialReference) return;
      const region = document.createElement('section');
      region.className = 'rd-size-guide__reference-region';
      const regionTitle = document.createElement('h4');
      regionTitle.textContent = label;
      const table = createBeddingTable(['Name', 'W × L'], metricReference.rows.map((row) => [row[0], row[1]]));
      Array.from(table.tBodies[0].rows).forEach((row, rowIndex) => {
        const valueCell = row.cells[1];
        valueCell.dataset.metric = formatBeddingDimension(metricReference.rows[rowIndex][1], 'metric');
        valueCell.dataset.imperial = formatBeddingDimension(imperialReference.rows[rowIndex][1], 'imperial');
      });
      region.append(regionTitle, table);
      referenceContent.append(region);
    });
    referenceDetails.append(referenceSummary, referenceContent);
    referenceDetails.addEventListener('toggle', () => {
      if (referenceDetails.open) window.requestAnimationFrame(() => updateTableScrollStates(container));
    });

    const contact = Array.from(container.children).find((element) => element.matches('p') && element.querySelector('a'));
    container.replaceChildren(intro, compatibilitySection, productSection, referenceDetails);
    if (contact) container.append(contact);
    container.classList.add('rd-size-guide--bedding-simple');
    container.dataset.roomdenBeddingSimple = 'true';

    const regionSelect = container.querySelector('[data-bedding-region]');
    const sheetSelect = container.querySelector('[data-bedding-sheet]');
    regionSelect.value = defaultBeddingRegion();
    sheetSelect.value = selectedProductOption('Sheet').toLowerCase() === 'fitted' ? 'fitted' : 'flat';

    function updateCompatibility() {
      const region = regionSelect.value;
      const labels = { us: 'US / CA', au: 'AU / NZ', uk: 'UK', eu: 'EU' };
      const columnIndex = compatibilityColumns[region];
      compatibilityTable.tHead.rows[0].cells[1].textContent = `${labels[region]} mattress`;
      Array.from(compatibilityTable.tBodies[0].rows).forEach((row, index) => {
        row.cells[1].textContent = compatibilityMetric.rows[index][columnIndex];
      });
    }

    function updateProductTable() {
      const sheet = sheetSelect.value;
      productTable.tHead.rows[0].cells[2].textContent = sheet === 'fitted' ? 'Fitted sheet' : 'Flat sheet';
      Array.from(productTable.tBodies[0].rows).forEach((row, index) => {
        const metricRow = productMetric.rows[index];
        const imperialRow = productImperial.rows[index];
        const sourceKeys = ['duvet', sheet, 'pillow'];
        sourceKeys.forEach((key, dataIndex) => {
          const cell = row.cells[dataIndex + 1];
          cell.dataset.metric = formatBeddingDimension(metricRow[metricColumns[key]], 'metric');
          cell.dataset.imperial = formatBeddingDimension(imperialRow[imperialColumns[key]], 'imperial');
        });
      });
      applySystem(container, container.dataset.activeSystem || 'metric');
      highlightSelectedBeddingSize(container);
    }

    regionSelect.addEventListener('change', updateCompatibility);
    sheetSelect.addEventListener('change', updateProductTable);
    updateCompatibility();
    updateProductTable();
  }

  function highlightSelectedBeddingSize(container) {
    const selectedSize = normalizeBeddingSize(selectedProductOption('Size'));
    container.querySelectorAll('[data-bedding-compatibility-table] tbody tr, [data-bedding-product-table] tbody tr').forEach((row) => {
      row.classList.toggle('rd-size-guide__row--selected', normalizeBeddingSize(row.cells[0]?.textContent) === selectedSize);
    });
  }

  function wrapTables(container) {
    container.querySelectorAll('table').forEach((table, index) => {
      table.classList.add('rd-size-guide__table');
      if (table.parentElement?.classList.contains('rd-size-guide__table-scroll')) {
        observeTableScrollState(table.parentElement);
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'rd-size-guide__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      const heading = table.previousElementSibling?.tagName === 'H4' ? table.previousElementSibling.textContent.trim() : `Size table ${index + 1}`;
      const columnCount = table.tHead?.rows[0]?.cells.length || table.rows[0]?.cells.length || 0;
      if (columnCount >= 2 && !table.querySelector('colgroup')) {
        const columnGroup = document.createElement('colgroup');
        Array.from({ length: columnCount }, (_, columnIndex) => {
          const column = document.createElement('col');
          column.className = columnIndex === 0 ? 'rd-size-guide__size-column' : 'rd-size-guide__data-column';
          columnGroup.append(column);
        });
        table.prepend(columnGroup);
      }
      wrapper.dataset.columnCount = String(columnCount);
      wrapper.dataset.tableLayout = columnCount <= 3 ? 'compact' : 'wide';
      if (columnCount > 3) {
        wrapper.style.setProperty('--rd-size-guide-table-min-inline-size', `${10 + (columnCount - 1) * 11.5}rem`);
      }
      wrapper.dataset.baseLabel = heading;
      wrapper.setAttribute('aria-label', heading);
      const hint = document.createElement('p');
      hint.className = 'rd-size-guide__scroll-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.hidden = true;
      table.before(hint);
      table.before(wrapper);
      wrapper.append(table);
    });
    container.querySelectorAll('.rd-size-guide__table-scroll').forEach(observeTableScrollState);
  }

  function applySystem(container, system, persist = false) {
    const normalizedSystem = system === 'imperial' ? 'imperial' : 'metric';
    const legacyUnit = normalizedSystem === 'imperial' ? 'in' : 'cm';
    container.dataset.activeSystem = normalizedSystem;
    container.dataset.activeUnit = legacyUnit;

    container.querySelectorAll('.unit-content-cm').forEach((element) => {
      element.hidden = normalizedSystem !== 'metric';
    });
    container.querySelectorAll('.unit-content-in').forEach((element) => {
      element.hidden = normalizedSystem !== 'imperial';
    });
    container.querySelectorAll('[data-metric][data-imperial]').forEach((cell) => {
      cell.textContent = cell.dataset[normalizedSystem] || '—';
    });
    container.querySelectorAll('[data-cm][data-in]').forEach((cell) => {
      cell.textContent = cell.dataset[legacyUnit] || '—';
    });
    container.querySelectorAll('[data-size-guide-system]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeGuideSystem === normalizedSystem));
    });
    container.querySelectorAll('[data-size-guide-toggle]').forEach((button) => {
      const nextSystem = normalizedSystem === 'metric' ? 'Imperial' : 'Metric';
      button.setAttribute('aria-label', `Switch to ${nextSystem} measurements`);
    });
    updateTableScrollStates(container);

    if (persist) storeSystem(normalizedSystem);
  }

  function buildUnitSwitch(container) {
    let control = container.querySelector('[data-size-guide-unit-switch]');
    if (!supportsUnits(container)) {
      if (control) control.hidden = true;
      return;
    }

    if (!control) {
      control = document.createElement('div');
      control.dataset.sizeGuideUnitSwitch = '';
      const intro = container.querySelector('.size-guide-intro');
      intro ? intro.after(control) : container.prepend(control);
    }

    control.hidden = false;
    control.setAttribute('role', 'group');
    control.setAttribute('aria-label', 'Measurement system');
    control.innerHTML = '<span class="rd-size-guide__unit-options"><button class="rd-size-guide__unit-button" type="button" data-size-guide-system="imperial" aria-pressed="false">Imperial</button><button class="rd-size-guide__unit-toggle" type="button" data-size-guide-toggle aria-label="Switch measurement system"><span class="rd-size-guide__unit-toggle-thumb" aria-hidden="true"></span></button><button class="rd-size-guide__unit-button" type="button" data-size-guide-system="metric" aria-pressed="false">Metric</button></span>';

    control.onclick = (event) => {
      const button = event.target.closest('[data-size-guide-system]');
      if (button) {
        applySystem(container, button.dataset.sizeGuideSystem, true);
        return;
      }

      if (event.target.closest('[data-size-guide-toggle]')) {
        applySystem(container, container.dataset.activeSystem === 'metric' ? 'imperial' : 'metric', true);
      }
    };
    control.onkeydown = (event) => {
      if (!['Enter', ' '].includes(event.key)) return;

      const button = event.target.closest('[data-size-guide-system]');
      const toggle = event.target.closest('[data-size-guide-toggle]');
      if (!button && !toggle) return;

      event.preventDefault();
      if (button) {
        applySystem(container, button.dataset.sizeGuideSystem, true);
      } else {
        applySystem(container, container.dataset.activeSystem === 'metric' ? 'imperial' : 'metric', true);
      }
    };
  }

  function init(container) {
    simplifyLegacyBedding(container);
    container.dataset.roomdenSizeGuideReady = 'true';
    container.dataset.roomdenSizeGuideVersion = COMPONENT_VERSION;
    wrapTables(container);
    buildUnitSwitch(container);
    const legacyDefault = container.dataset.defaultUnit === 'in' ? 'imperial' : 'metric';
    applySystem(container, savedSystem() || container.dataset.defaultSystem || legacyDefault);
    highlightSelectedBeddingSize(container);
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-roomden-size-guide]').forEach(init);
  }

  function openFromTrigger(trigger) {
    const targetId = trigger.getAttribute('aria-controls');
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    if (!target.hasAttribute('open')) {
      target.querySelector('summary')?.click();
    }
    window.requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
      window.setTimeout(() => target.querySelector('summary')?.focus({ preventScroll: true }), 320);
    });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-roomden-size-guide-trigger]');
    if (!trigger) return;
    event.preventDefault();
    openFromTrigger(trigger);
  });

  document.addEventListener('keydown', (event) => {
    const trigger = event.target.closest('[data-roomden-size-guide-trigger]');
    if (!trigger || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openFromTrigger(trigger);
  });

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.addEventListener('pageshow', () => initAll());
  document.addEventListener('toggle', (event) => {
    const accordion = event.target.closest?.('[data-size-guide-accordion]');
    if (!accordion?.open) return;
    window.requestAnimationFrame(() => accordion.querySelectorAll('[data-roomden-size-guide]').forEach(updateTableScrollStates));
  }, true);
  document.addEventListener('change', (event) => {
    if (!event.target.matches('input[type="radio"][name^="Size-"], input[type="radio"][name^="Sheet-"]')) return;
    document.querySelectorAll('[data-roomden-size-guide]').forEach((container) => {
      if (event.target.name.startsWith('Sheet-')) {
        const select = container.querySelector('[data-bedding-sheet]');
        if (select) {
          select.value = event.target.value.toLowerCase() === 'fitted' ? 'fitted' : 'flat';
          select.dispatchEvent(new Event('change'));
        }
      }
      highlightSelectedBeddingSize(container);
    });
  });

  const observer = new MutationObserver((mutations) => {
    const needsInit = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('[data-roomden-size-guide]') || node.querySelector?.('[data-roomden-size-guide]'))
      )
    );
    if (needsInit) window.requestAnimationFrame(() => initAll());
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.RoomdenSizeGuide = { version: COMPONENT_VERSION, initAll, openFromTrigger };
  initAll();
})();
